/**
 * DiagramPickerModal
 * 
 * Modal for inserting diagrams into documents.
 * Supports creating new diagrams and inserting from project diagrams.
 */

import React, { useState, useEffect } from 'react';
import { projectsApi, MermaidDiagram } from '../../services/api';
import { logger } from '../../utils/logger';
import { MERMAID_TEMPLATES, MERMAID_TYPE_CONFIG, MERMAID_DIAGRAM_TYPES } from '../../config/mermaid-templates';
import type { MermaidDiagramType } from '../../types/panel-types';

interface DiagramPickerModalProps {
  projectId: string;
  onClose: () => void;
  onInsert: (diagram: {
    content: string;
    caption?: string;
    diagramId?: string;
    diagramVersion?: string;
    diagramTitle?: string;
  }) => void;
}

type TabType = 'new' | 'project';

export const DiagramPickerModal: React.FC<DiagramPickerModalProps> = ({
  projectId,
  onClose,
  onInsert,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('new');
  const [projectDiagrams, setProjectDiagrams] = useState<MermaidDiagram[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load project diagrams when switching to project tab
  useEffect(() => {
    if (activeTab === 'project' && projectId) {
      setLoading(true);
      setError(null);
      
      projectsApi.listMermaidDiagrams(projectId)
        .then((diagrams) => {
          setProjectDiagrams(diagrams);
        })
        .catch((err) => {
          logger.error('[DiagramPicker] Failed to load project diagrams:', err);
          setError('Failed to load project diagrams');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [activeTab, projectId]);

  // Handle inserting a new empty diagram
  const handleInsertNew = (diagramType: MermaidDiagramType) => {
    onInsert({
      content: MERMAID_TEMPLATES[diagramType] || MERMAID_TEMPLATES.flowchart,
    });
    onClose();
  };

  // Handle inserting from project diagram
  const handleInsertFromProject = (diagram: MermaidDiagram) => {
    onInsert({
      content: diagram.mermaidSource,
      caption: diagram.documentTitle,
      diagramId: diagram.diagramId,
      diagramVersion: diagram.revision,
      diagramTitle: diagram.documentTitle,
    });
    onClose();
  };

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="diagram-picker-overlay" onClick={onClose}>
      <div className="diagram-picker-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="diagram-picker-header">
          <h2>Insert Diagram</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="diagram-picker-tabs">
          <button
            className={`tab-btn ${activeTab === 'new' ? 'active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            New Diagram
          </button>
          <button
            className={`tab-btn ${activeTab === 'project' ? 'active' : ''}`}
            onClick={() => setActiveTab('project')}
          >
            From Project
          </button>
        </div>

        {/* Content */}
        <div className="diagram-picker-content">
          {activeTab === 'new' ? (
            <div className="new-diagram-section">
              <p className="section-description">
                Choose a diagram template to insert. You can edit the code in the document.
              </p>
              <div className="template-grid">
                {MERMAID_DIAGRAM_TYPES.map((type) => {
                  const config = MERMAID_TYPE_CONFIG[type];
                  return (
                    <button
                      key={type}
                      className="template-card"
                      onClick={() => handleInsertNew(type)}
                    >
                      <div className="template-icon">{config.icon}</div>
                      <div className="template-name">{config.label}</div>
                      <div className="template-desc">{config.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="project-diagram-section">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <span>Loading project diagrams...</span>
                </div>
              ) : error ? (
                <div className="error-state">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              ) : projectDiagrams.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📊</div>
                  <p>No diagrams in this project.</p>
                  <p className="empty-hint">Create diagrams in the Mermaid Diagrams section.</p>
                </div>
              ) : (
                <div className="diagram-list">
                  {projectDiagrams.map((diagram) => (
                    <button
                      key={diagram.diagramId}
                      className="diagram-card"
                      onClick={() => handleInsertFromProject(diagram)}
                    >
                      {diagram.svgUrl ? (
                        <div className="diagram-preview">
                          <img src={diagram.svgUrl} alt={diagram.documentTitle} />
                        </div>
                      ) : (
                        <div className="diagram-preview-placeholder">
                          <span>📊</span>
                        </div>
                      )}
                      <div className="diagram-info">
                        <div className="diagram-title">{diagram.documentTitle}</div>
                        <div className="diagram-meta">
                          <span className="diagram-type">{diagram.mermaidType}</span>
                          <span className="diagram-version">Rev {diagram.revision}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
